"use client"

import { useMemo, useState } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { FIELD_UNDERLINE, FieldShell } from "@/components/graphite/field"
import { PageAction } from "@/components/graphite/page-header"
import { QuietAction } from "@/components/graphite/surface"
import { cn } from "@/lib/utils"
import { MailPlus, Send, X } from "lucide-react"
import { useGetContacts, usePostEmailsSendEmail } from "openapi/api/endpoints"
import type { Contact } from "openapi/api/model"
import { toast } from "sonner"
import { useNavigate } from "@tanstack/react-router"

function formatContactName(contact: Contact) {
  return `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
}

export function QuickEmailDialog() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [to, setTo] = useState("")
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)

  const { data: contacts = [] } = useGetContacts(undefined, { query: { enabled: open } })
  const { mutateAsync: sendEmail, isPending: isSending } = usePostEmailsSendEmail()

  const contactSuggestions = useMemo(() => {
    const query = to.trim().toLowerCase()
    const base = contacts.filter((contact) => !!contact.email)

    if (!query) return base.slice(0, 6)

    return base
      .filter((contact) => {
        const fullName = formatContactName(contact).toLowerCase()
        const email = (contact.email ?? "").toLowerCase()
        return fullName.includes(query) || email.includes(query)
      })
      .slice(0, 6)
  }, [contacts, to])

  const resetForm = () => {
    setTo("")
    setSubject("")
    setMessage("")
    setSelectedContact(null)
    setShowSuggestions(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      resetForm()
    }
  }

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact)
    setTo(contact.email ?? "")
    setShowSuggestions(false)
  }

  const handleToChange = (value: string) => {
    setTo(value)
    setShowSuggestions(true)
    if (selectedContact?.email !== value) {
      setSelectedContact(null)
    }
  }

  const handleSend = async () => {
    if (!to.trim()) {
      toast.error("Recipient is required")
      return
    }
    if (!subject.trim()) {
      toast.error("Subject is required")
      return
    }
    if (!message.trim()) {
      toast.error("Message is required")
      return
    }

    try {
      await sendEmail({
        data: {
          recipientEmail: to.trim(),
          subject: subject.trim(),
          message: message.trim(),
          contact_id: selectedContact?.id ?? null,
          first_name: selectedContact?.first_name ?? null,
          last_name: selectedContact?.last_name ?? null,
        },
      })

      toast.success("Email sent successfully")
      handleOpenChange(false)
    } catch (err) {
      toast.error("Failed to send email")
      console.error("Failed to send quick email:", err)
    }
  }

  return (
    <>
      {/* The trigger lives in the top bar, so it takes the bar's quiet
          treatment rather than an outlined button of its own. */}
      <QuietAction onClick={() => setOpen(true)}>
        <MailPlus className="size-4" />
        <span className="hidden @sm:inline">Quick Email</span>
      </QuietAction>

      <DialogShell
        open={open}
        onOpenChange={handleOpenChange}
        wide
        icon={<MailPlus className="size-4" />}
        title="Quick Email"
        footer={
          <>
            <QuietAction size="md" onClick={() => handleOpenChange(false)}>
              Cancel
            </QuietAction>
            <PageAction tone="primary" onClick={handleSend} disabled={isSending}>
              <Send className="size-[15px]" />
              {isSending ? "Sending…" : "Send"}
            </PageAction>
          </>
        }
      >
        <FieldShell label="To">
          <div className="relative">
            <input
              id="quick-email-to"
              value={to}
              onChange={(e) => handleToChange(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Type name or email"
              aria-label="To"
              className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
            />

            {showSuggestions && contactSuggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-[8px] border bg-card shadow-md">
                {contactSuggestions.map((contact) => {
                  const fullName = formatContactName(contact)
                  return (
                    <button
                      key={contact.id}
                      type="button"
                      className="flex w-full items-baseline justify-between gap-3 px-2.5 py-2 text-left transition-colors hover:bg-foreground/[0.04]"
                      onClick={() => handleSelectContact(contact)}
                    >
                      <span className="truncate text-[12.5px]">{fullName || contact.email}</span>
                      <span className="truncate font-mono text-[11px] text-muted-foreground">
                        {contact.email}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {selectedContact && (
            <div className="mt-2 flex items-center justify-between gap-2 text-[11.5px] text-muted-foreground">
              <span className="truncate">
                {formatContactName(selectedContact) || selectedContact.email}
              </span>
              <span className="flex flex-none items-center gap-1">
                <button
                  type="button"
                  className="text-brand-ink transition-opacity hover:opacity-80"
                  onClick={() => {
                    handleOpenChange(false)
                    navigate({ to: `/persons/${selectedContact.id}` })
                  }}
                >
                  View profile
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedContact(null)
                    setShowSuggestions(true)
                  }}
                  className="rounded p-0.5 transition-colors hover:text-destructive"
                  aria-label="Clear selected contact"
                >
                  <X className="size-3" />
                </button>
              </span>
            </div>
          )}
        </FieldShell>

        <FieldShell label="Subject" className="mt-5">
          <input
            id="quick-email-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter subject"
            aria-label="Subject"
            className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
          />
        </FieldShell>

        <FieldShell label="Message" className="mt-5">
          <textarea
            id="quick-email-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message..."
            rows={10}
            aria-label="Message"
            className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
          />
        </FieldShell>
      </DialogShell>
    </>
  )
}
